import React, { useEffect } from "react";
import { LegalLayout, LegalH2, LegalP, LegalUl } from "../components/LegalLayout";

const Privacy = () => {
    useEffect(() => {
        document.title = "Privacy Policy — Voro";
        window.scrollTo(0, 0);
    }, []);

    return (
        <LegalLayout
            testId="page-privacy"
            title="Privacy Policy"
            updated="11 June 2026"
        >
            <LegalP>
                Voro Pty Ltd (<strong>"Voro"</strong>, <strong>"we"</strong>,{" "}
                <strong>"us"</strong>, <strong>"our"</strong>) respects your privacy and is
                committed to handling your personal information in accordance with the{" "}
                <em>Privacy Act 1988</em> (Cth) and the Australian Privacy Principles (APPs).
                This Privacy Policy explains what information we collect via{" "}
                <a href="https://voro.au" className="text-violet-300 hover:underline">
                    voro.au
                </a>{" "}
                and the Voro mobile application (the <strong>"Service"</strong>), how we use
                it, and your rights in relation to it.
            </LegalP>

            <LegalH2>1. Information we collect</LegalH2>
            <LegalP>
                During the current waitlist phase, we collect a limited set of personal
                information when you opt in:
            </LegalP>
            <LegalUl>
                <li>
                    <strong>Identity & contact details</strong> — your first name and email
                    address, submitted via our waitlist form.
                </li>
                <li>
                    <strong>Technical data</strong> — anonymised information automatically
                    collected by your browser when you visit voro.au, including IP address,
                    device type, browser, referring page, and timestamps.
                </li>
                <li>
                    <strong>Cookies & analytics</strong> — small data files used to remember
                    preferences and measure traffic. You can disable cookies via your browser
                    settings; some site functionality may be reduced.
                </li>
            </LegalUl>
            <LegalP>
                When the Voro app launches, additional information (such as workout activity,
                location while using the app, gym affiliation, and profile content) will be
                collected. This Policy will be updated to reflect that scope before launch.
            </LegalP>

            <LegalH2>2. How we use your information</LegalH2>
            <LegalUl>
                <li>To add you to the Voro waitlist and notify you about launch.</li>
                <li>
                    To send product updates, founding-athlete invites, and occasional
                    marketing emails (you can unsubscribe at any time).
                </li>
                <li>To improve the website, fix bugs, and understand visitor behaviour.</li>
                <li>To comply with our legal obligations under Australian law.</li>
            </LegalUl>

            <LegalH2>3. Mailchimp & overseas data storage</LegalH2>
            <LegalP>
                We use Mailchimp (operated by Intuit Inc.) to manage our waitlist and email
                communications. When you submit your email, your name and email address are
                transferred to and stored on Mailchimp servers located in the United States.
                Mailchimp is contractually obligated to handle your data in line with its{" "}
                <a
                    href="https://www.intuit.com/privacy/statement/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-violet-300 hover:underline"
                >
                    privacy statement
                </a>{" "}
                and applicable data-protection laws. By joining the waitlist you consent to
                this overseas transfer.
            </LegalP>

            <LegalH2>4. Sharing your information</LegalH2>
            <LegalP>
                We do not sell your personal information. We only share it with:
            </LegalP>
            <LegalUl>
                <li>Trusted service providers (such as Mailchimp and our hosting infrastructure) who help us operate the Service;</li>
                <li>Law enforcement or regulators where required by law; and</li>
                <li>A successor entity in the event of a business sale, merger, or restructure (subject to equivalent privacy protections).</li>
            </LegalUl>

            <LegalH2>5. Data security</LegalH2>
            <LegalP>
                We take reasonable steps to protect your information from misuse, loss, and
                unauthorised access — including encrypted transit (HTTPS/TLS), restricted
                staff access, and reputable third-party processors. No internet-based service
                can guarantee absolute security, so we cannot promise that all data will be
                immune to all risks.
            </LegalP>

            <LegalH2>6. How long we keep your information</LegalH2>
            <LegalP>
                We retain your waitlist details until you ask us to delete them, you
                unsubscribe and request removal, or we no longer have a legitimate reason to
                hold them — whichever happens first.
            </LegalP>

            <LegalH2>7. Your rights</LegalH2>
            <LegalP>Under the Privacy Act and APPs you can:</LegalP>
            <LegalUl>
                <li>Request access to the personal information we hold about you;</li>
                <li>Ask us to correct information that is inaccurate or out of date;</li>
                <li>Ask us to delete your information (subject to legal retention requirements); and</li>
                <li>
                    Make a privacy complaint. If you're not satisfied with our response, you
                    can contact the Office of the Australian Information Commissioner (OAIC)
                    at{" "}
                    <a
                        href="https://www.oaic.gov.au"
                        target="_blank"
                        rel="noreferrer"
                        className="text-violet-300 hover:underline"
                    >
                        oaic.gov.au
                    </a>
                    .
                </li>
            </LegalUl>
            <LegalP>
                To exercise any of these rights, email us at{" "}
                <a
                    href="mailto:hello@voro.au"
                    className="text-violet-300 hover:underline"
                >
                    hello@voro.au
                </a>
                . We aim to respond within 30 days.
            </LegalP>

            <LegalH2>8. Children</LegalH2>
            <LegalP>
                Voro is intended for users aged 16 and over. We do not knowingly collect
                personal information from children under 16. If you believe a minor has
                provided us with information, please contact us so we can delete it.
            </LegalP>

            <LegalH2>9. Changes to this Policy</LegalH2>
            <LegalP>
                We may update this Privacy Policy from time to time — particularly when we
                launch the full app. The "Last updated" date at the top of this page will
                always reflect the current version. Material changes will be communicated via
                email or a notice on voro.au.
            </LegalP>

            <LegalH2>10. Contact us</LegalH2>
            <LegalP>
                Questions or requests about your information? Reach our team at{" "}
                <a
                    href="mailto:hello@voro.au"
                    className="text-violet-300 hover:underline"
                >
                    hello@voro.au
                </a>
                .
            </LegalP>
        </LegalLayout>
    );
};

export default Privacy;
